import { inject, InjectionToken, LOCALE_ID } from '@angular/core';

/**
 * Injection token for the locale used by the DatePicker. Defaults to Angular's LOCALE_ID if not provided.
 *
 * @example
 *   ```typescript
 *   providers: [
 *     { provide: HX_DATE_LOCALE, useValue: 'fr-FR' }
 *   ]
 *   ```;
 */
export const HX_DATE_LOCALE = new InjectionToken<string>('HX_DATE_LOCALE', {
  providedIn: 'root',
  factory: () => inject(LOCALE_ID),
});

/** Date format patterns for parsing and displaying dates. */
export type DateFormats = {
  /** Patterns to try when parsing date input text. Multiple formats can be provided as fallbacks. */
  parse: string[];

  /** Format pattern for displaying dates in the input field. */
  display: string;
};

/**
 * Injection token for date format patterns.
 *
 * @example
 *   ```typescript
 *   providers: [
 *     {
 *       provide: HX_DATE_FORMATS,
 *       useValue: {
 *         parse: ['MM/DD/YYYY', 'M/D/YY'],
 *         display: 'MM/DD/YYYY'
 *       }
 *     }
 *   ]
 *   ```;
 */
export const HX_DATE_FORMATS = new InjectionToken<DateFormats>('HX_DATE_FORMATS', {
  providedIn: 'root',
  factory: () => ({
    parse: ['MM/DD/YYYY', 'M/D/YY', 'YYYY-MM-DD'],
    display: 'MM/DD/YYYY',
  }),
});

/**
 * Injection token for the DateAdapter implementation. Determines which date manipulation library is used.
 *
 * @example
 *   ```typescript
 *   providers: [
 *     { provide: HX_DATE_ADAPTER, useClass: LuxonAdapter }
 *   ]
 *   ```;
 */
export const HX_DATE_ADAPTER = new InjectionToken<any>('HX_DATE_ADAPTER');
